/*
 * Copyright (c) 2021. Dave van Rijn Development
 */
import './style/DVRSlider.scss';

import React, {CSSProperties, MouseEventHandler, PureComponent} from 'react';
import {ChangeFunction, Colors, convertColor, generateComponentId} from "../../..";
import {ElementPosition} from '../util/interfaces';
import {ControlContext} from "../util/controlContext";
import classNames from "classnames";

interface Props {
    onChange: ChangeFunction;
    onMouseEnter?: MouseEventHandler;
    onMouseLeave?: MouseEventHandler;
    value?: number;
    disabled: boolean;
    id?: string;
    className: string;
    labelClassName: string;
    style?: CSSProperties;
    asNumber?: boolean;
    label?: string;
    labelPosition: ElementPosition;
    inputProps?: React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
}

export default class DVRSlider extends PureComponent<Props> {
    id: string;
    static defaultProps = {
        min: 0,
        max: 100,
        step: 1,
        disabled: false,
        className: '',
        labelClassName: '',
        labelPosition: ElementPosition.RIGHT,
    };
    declare context: React.ContextType<typeof ControlContext>;
    static contextType = ControlContext;

    constructor(props: Props) {
        super(props);
        this.id = generateComponentId(props.id);
    }

    onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const {onChange, asNumber} = this.props, {value} = evt.target;
        if (asNumber) onChange(Number(value), evt);
        else onChange(value, evt);
    };

    onMouseEnter = (evt: React.MouseEvent) => {
        const {onMouseEnter} = this.props;
        this.setState({hovered: true});
        if (onMouseEnter) onMouseEnter(evt);
    }

    onMouseLeave = (evt: React.MouseEvent) => {
        const {onMouseLeave} = this.props;
        this.setState({hovered: false});
        if (onMouseLeave) onMouseLeave(evt);
    };

    getInputProps = (): React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> => {
        const {inputProps} = this.props, defaultProps = {min: 0, max: 100, step: 1};
        return Object.assign({}, defaultProps, inputProps);
    }

    getTrackColor = () => {
        const {disabled} = this.props;
        if (disabled) return Colors.GRAY4;
        return convertColor(this.context.baseColor);
    };

    getControlColor = () => convertColor(this.context.baseColor);

    componentDidMount = () => {
        const input = document.getElementById(`${this.id}-input`);
        if (input) {
            window.setTimeout(() => {
                input.style.setProperty('--control-color', this.getControlColor())
            });
        }
    };

    renderLabel = () => {
        const {label} = this.props;
        if (!label?.length) return null;
        return <label className='dvr-slider-label'>{label}</label>;
    };

    render = () => {
        const {value, disabled, className, style, labelPosition, label} = this.props;
        return (
            <div className={classNames('dvr-slider-container', className, label?.length && labelPosition,
                disabled && 'disabled')}
                 id={this.id}>
                {this.renderLabel()}
                <input type='range' value={value} id={`${this.id}-input`} onChange={this.onChange}
                       disabled={disabled} className={classNames('dvr-slider', disabled && 'disabled')}
                       style={{backgroundColor: this.getTrackColor(), ...style}} onMouseEnter={this.onMouseEnter}
                       onMouseLeave={this.onMouseLeave} {...this.getInputProps()}/>
            </div>
        )
    }
}